/**
 * NpcShopModal.ts - DAoC Style Merchant Shop Window
 * 
 * 월드에서 상인 NPC를 직접 클릭했을 때 독립적으로 열리는 전용 상점 창.
 * 내 인벤토리가 아닌 해당 상인의 상품 목록을 확인하고 직접 구매할 수 있습니다.
 */

import { EntityState } from '../../core/Types';
import { Currency } from '../../core/Currency';
import { EventBus } from '../../core/EventBus';
import { initEntityInventory20, buyItemFromNpcInventory } from '../character/CharacterSystem';

export class NpcShopModal {
  private el: HTMLElement | null = null;
  private isOpen = false;
  private player: EntityState | null = null;
  private npc: EntityState | null = null;
  private partySize: number = 1;

  constructor() {
    this.createDom();
  }

  private createDom(): void {
    if (document.getElementById('daoc-npc-shop-modal')) return;

    const wrap = document.createElement('div');
    wrap.id = 'daoc-npc-shop-modal';
    wrap.style.cssText = `
      position: fixed;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      width: 520px;
      max-width: 95vw;
      max-height: 85vh;
      background: linear-gradient(180deg, #2b2218, #18120b);
      border: 2px solid #7a5c2e;
      border-radius: 6px;
      box-shadow: 0 0 0 1px #000, inset 0 0 0 1px #c9a45a44, 0 12px 30px rgba(0,0,0,0.8);
      color: #e8dcc0;
      font-family: sans-serif;
      z-index: 9999;
      display: none;
      flex-direction: column;
      user-select: none;
    `;

    document.body.appendChild(wrap);
    this.el = wrap;
  }

  public open(player: EntityState, npc: EntityState, partySize: number = 1): void {
    // 오직 마구간지기(말 상인)만 승마표 및 군마 관리용품 상점을 운영합니다.
    const isStable = Boolean(
      npc.id.includes('stable') ||
      (npc.job && npc.job.includes('마구간')) ||
      (npc.name && npc.name.includes('마구간'))
    );

    if (!isStable) {
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `💬 [${npc.name}]: "저희는 물건을 팔지 않습니다. 모든 물품은 직접 제작하거나 유저들과 거래하세요!"`,
        category: 'system',
      });
      return;
    }

    this.player = player;
    this.npc = npc;
    this.partySize = Math.max(1, partySize);
    initEntityInventory20(npc);
    initEntityInventory20(player);

    this.isOpen = true;
    this.render();
  }

  public close(): void {
    this.isOpen = false;
    if (this.el) {
      this.el.style.display = 'none';
      this.el.innerHTML = '';
    }
  }

  public render(): void {
    if (!this.el || !this.player || !this.npc || !this.isOpen) return;
    this.el.style.display = 'flex';

    const npc = this.npc;
    const player = this.player;
    const slots = npc.inventory || [];
    const partySize = this.partySize;

    const rarityColors: Record<string, { border: string; bg: string }> = {
      common: { border: '#475569', bg: 'rgba(30, 41, 59, 0.85)' },
      fine: { border: '#22c55e', bg: 'rgba(6, 78, 59, 0.45)' },
      masterwork: { border: '#38bdf8', bg: 'rgba(12, 74, 110, 0.5)' },
      legendary: { border: '#f59e0b', bg: 'rgba(120, 53, 15, 0.55)' },
    };

    const validItems = slots
      .map((item, idx) => ({ item, idx }))
      .filter((entry) => entry.item !== null);

    this.el.innerHTML = `
      <!-- Titlebar -->
      <div style="background:linear-gradient(#5a431f,#3a2a12); border-bottom:1px solid #000; padding:8px 12px; display:flex; justify-content:space-between; align-items:center; font-size:12.5px; font-weight:bold; color:#f3d98b; letter-spacing:0.5px;">
        <div style="display:flex; align-items:center; gap:8px;">
          <span style="font-size:18px;">${npc.avatarIcon || '🏛️'}</span>
          <span>${npc.name} (${npc.job}) — 상점</span>
        </div>
        <button id="btn-npc-shop-close" style="background:none; border:none; color:#f3d98b; font-size:15px; cursor:pointer; font-weight:bold;">✕</button>
      </div>

      <!-- Buyer Currency Status -->
      <div style="background:#140e08; border-bottom:1px solid #3f2f17; padding:6px 14px; display:flex; justify-content:space-between; align-items:center; font-size:11px;">
        <span style="color:#cdb98a;">구매자: <b style="color:#38bdf8;">${player.name}</b></span>
        <div style="display:flex; align-items:center; gap:6px;">
          <span style="color:#94a3b8;">내 소지금:</span>
          <span style="background:#241b10; border:1px solid #5a431f; padding:2px 6px; border-radius:3px;">
            ${Currency.format(player.gold ?? 0)}
          </span>
        </div>
      </div>

      <!-- Product List Grid -->
      <div style="padding:14px; overflow-y:auto; max-height:420px; display:grid; grid-template-columns:repeat(4, 1fr); gap:8px; background:rgba(0,0,0,0.3);">
        ${
          validItems.length === 0
            ? `<div style="grid-column:1/-1; text-align:center; color:#94a3b8; padding:30px 0;">판매 중인 상품이 없습니다.</div>`
            : validItems
                .map(({ item, idx }) => {
                  if (!item) return '';
                  const rStyle = rarityColors[item.rarity] || rarityColors.common;
                  const isHorseTicket = Boolean(
                    item.routeTargetStableId || item.name.includes('승마표') || item.name.includes('마패')
                  );
                  const buyTotalCost = isHorseTicket ? item.valueGold * partySize : item.valueGold;

                  return `
                    <div title="${item.name}\n${item.description}\n${isHorseTicket ? `1인당 ${Currency.format(item.valueGold)} × ${partySize}명 = 총 ${Currency.format(buyTotalCost)} (파티리더 전액 부담)` : `단가: ${Currency.format(item.valueGold)}`}" style="
                      background:${rStyle.bg};
                      border:1.5px solid ${rStyle.border};
                      border-radius:6px;
                      padding:6px;
                      display:flex;
                      flex-direction:column;
                      justify-content:space-between;
                      align-items:center;
                      min-height:92px;
                      text-align:center;
                    ">
                      <div style="width:100%; display:flex; justify-content:space-between; align-items:center;">
                        <span style="font-size:8px; color:#94a3b8;">재고</span>
                        <span style="font-size:8.5px; font-weight:bold; color:#fde047; background:rgba(0,0,0,0.55); padding:0 3px; border-radius:3px;">x${item.quantity}</span>
                      </div>
                      <div style="font-size:22px; margin:2px 0;">${item.icon}</div>
                      <div style="font-size:9.5px; font-weight:bold; color:#f8fafc; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; width:100%;">
                        ${item.name}
                      </div>
                      <div style="width:100%; margin-top:4px;">
                        <button data-shop-buy-idx="${idx}" style="
                          width:100%;
                          background:#0284c7;
                          border:1px solid #7dd3fc;
                          color:#fff;
                          font-size:8px;
                          padding:3px 2px;
                          border-radius:4px;
                          cursor:pointer;
                          font-weight:bold;
                        ">
                          💰${Currency.format(buyTotalCost)} 구매
                        </button>
                      </div>
                    </div>
                  `;
                })
                .join('')
        }
      </div>

      <!-- Footer Info -->
      <div style="background:#18120b; border-top:1px solid #3f2f17; padding:8px 14px; display:flex; justify-content:space-between; align-items:center; font-size:10.5px; color:#94a3b8;">
        <span>💡 승마표 구매 시 파티 ${partySize}인분 요금이 자동 계산됩니다.</span>
        <button id="btn-npc-shop-ok" style="background:#2563eb; border:1px solid #60a5fa; color:#fff; padding:4px 14px; border-radius:4px; font-weight:bold; cursor:pointer;">
          닫기
        </button>
      </div>
    `;

    this.bindEvents();
  }

  private bindEvents(): void {
    if (!this.el || !this.player || !this.npc) return;

    this.el.querySelector('#btn-npc-shop-close')?.addEventListener('click', () => this.close());
    this.el.querySelector('#btn-npc-shop-ok')?.addEventListener('click', () => this.close());

    this.el.querySelectorAll('[data-shop-buy-idx]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (!this.player || !this.npc) return;
        const idx = parseInt(btn.getAttribute('data-shop-buy-idx') || '0', 10);
        buyItemFromNpcInventory(this.player, this.npc, idx, this.partySize);
        this.render();
      });
    });
  }
}

export const npcShopModalInstance = new NpcShopModal();
